import { useMemo, useState } from "react";
import {
    useCounterpartiesQuery,
    useCounterpartyPickerQuery,
} from "@api/counterparty.api";
import { DirectorySort } from "@common/constants/counterparty";
import { RegisteredCounterpartyIncludeScope } from "@common/helpers/counterparties";
import { SEARCH_DEBOUNCE_TIME } from "@constants";
import type { PickerStatus } from "@design-system/Picker/Picker.types";
import { toPickerLoadMore } from "@helpers/pickerLoadMore";
import { useDebouncedEffect } from "@hooks/useDebouncedEffect";

/**
 * Rows and paging for `CounterpartyDropdown`: used counterparties by name, 50
 * at a time, searched on the server while the popup is open. The selected ids
 * are looked up on their own so their names show even when their rows are not
 * loaded.
 */
export const useCounterpartyDropdownOptions = ({
    isOpen,
    selectedIds,
}: {
    isOpen: boolean;
    selectedIds: string[];
}) => {
    const [typedSearch, setTypedSearch] = useState<string>();
    const [search, setSearch] = useState<string>();

    useDebouncedEffect(
        () => {
            setSearch(typedSearch);
        },
        [typedSearch],
        SEARCH_DEBOUNCE_TIME,
    );

    const counterpartiesQuery = useCounterpartyPickerQuery(
        {
            search,
            sort: DirectorySort.NAME_ASC,
            includeRegistered: RegisteredCounterpartyIncludeScope.USED,
        },
        { enabled: isOpen },
    );
    const selectedCounterpartiesQuery = useCounterpartiesQuery(
        {
            counterpartiesIds: selectedIds,
            includeRegistered: RegisteredCounterpartyIncludeScope.ALL,
        },
        { enabled: selectedIds.length > 0 },
    );

    const loadedCounterparties = counterpartiesQuery.data;
    const items = useMemo(
        () =>
            (loadedCounterparties ?? []).map((counterparty) => ({
                value: counterparty.id,
                label: counterparty.name,
            })),
        [loadedCounterparties],
    );

    const selectedCounterpartyRows = selectedCounterpartiesQuery.data?.data;
    const namesById = useMemo(
        () =>
            new Map(
                [
                    ...(loadedCounterparties ?? []),
                    ...(selectedCounterpartyRows ?? []).flatMap(
                        ({ counterparty }) =>
                            counterparty ? [counterparty] : [],
                    ),
                ].map((counterparty) => [counterparty.id, counterparty.name]),
            ),
        [selectedCounterpartyRows, loadedCounterparties],
    );

    return {
        items,
        namesById,
        status: getListStatus(counterpartiesQuery),
        loadMore: toPickerLoadMore(counterpartiesQuery),
        onInputValueChange: (inputValue: string) =>
            setTypedSearch(inputValue || undefined),
    };
};

const getListStatus = ({
    isPending,
    isLoadingError,
}: {
    isPending: boolean;
    isLoadingError: boolean;
}): PickerStatus => {
    if (isPending) {
        return "loading";
    }
    return isLoadingError ? "error" : "idle";
};
